import { useState, useEffect, useCallback, useRef } from 'react'
import { Capacitor } from '@capacitor/core'
import { readAdminToken } from './adminAuth.js'
import { readPastorToken } from './pastorAuth.js'

const API = import.meta.env.VITE_API_URL || ''
const UNREAD_POLL_MS = 15_000

/** Fetch-Helfer für /api/chat mit dem Token des eingeloggten Teammitglieds */
export function createChatApi(getToken) {
  return async function chatApi(path, options = {}) {
    const res = await fetch(`${API}/api/chat${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getToken()}`,
        ...options.headers,
      },
    })
    if (res.status === 204) return null
    const data = await res.json().catch(() => ({}))
    if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`)
    return data
  }
}

// ── Push-Geräte für Chat-Benachrichtigungen ──────────────────────────────────
function storedPushToken() {
  try { return localStorage.getItem('reverend_push_token') || '' } catch { return '' }
}

/** Meldet das Gerät für alle auf ihm eingeloggten Teamkonten an (Admin und/oder Seelsorger) */
export async function registerStaffDevice(pushToken = storedPushToken()) {
  if (!pushToken) return
  for (const token of [readAdminToken(), readPastorToken()].filter(Boolean)) {
    try {
      await createChatApi(() => token)('/devices', { method: 'POST', body: JSON.stringify({ token: pushToken }) })
    } catch { /* nicht kritisch */ }
  }
}

/** Vor dem Logout aufrufen, damit keine Chat-Pushes mehr ans Gerät gehen */
export async function unregisterStaffDevice(authToken) {
  const pushToken = storedPushToken()
  if (!pushToken || !authToken) return
  try {
    await createChatApi(() => authToken)('/devices/remove', { method: 'POST', body: JSON.stringify({ token: pushToken }) })
  } catch { /* nicht kritisch */ }
}

// ── Ungelesen-Zähler (Badge, Tab-Titel, Browser-Benachrichtigung) ────────────
export function useChatUnread(getToken, enabled = true) {
  const [unread, setUnread] = useState(0)
  const latestRef = useRef(null)
  const api = useRef(createChatApi(getToken)).current

  const refresh = useCallback(async () => {
    if (!enabled || !getToken()) return
    try {
      const { unread: count, latest_id: latest } = await api('/unread')
      const isNew = latestRef.current !== null && latest > latestRef.current
      latestRef.current = latest
      setUnread(count)

      // Im Web: Desktop-Benachrichtigung, wenn der Tab im Hintergrund ist
      if (isNew && count > 0 && document.hidden && !Capacitor.isNativePlatform()
          && typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        new Notification('Reverend', { body: count === 1 ? 'New message' : `${count} unread messages`, tag: 'reverend-chat' })
      }
    } catch { /* offline o. ä. – beim nächsten Mal wieder */ }
  }, [api, enabled, getToken])

  useEffect(() => {
    refresh()
    const timer = setInterval(refresh, UNREAD_POLL_MS)
    const onVisible = () => { if (!document.hidden) refresh() }
    document.addEventListener('visibilitychange', onVisible)
    return () => { clearInterval(timer); document.removeEventListener('visibilitychange', onVisible) }
  }, [refresh])

  // Tab-Titel wie bei Messenger: "(3) Reverend"
  useEffect(() => {
    const base = document.title.replace(/^\(\d+\)\s*/, '')
    document.title = unread > 0 ? `(${unread}) ${base}` : base
  }, [unread])

  return { unread, refresh }
}

/** Im Web einmalig um Erlaubnis für Desktop-Benachrichtigungen bitten */
export function requestWebNotificationPermission() {
  if (Capacitor.isNativePlatform() || typeof Notification === 'undefined') return
  if (Notification.permission === 'default') Notification.requestPermission().catch(() => {})
}
